/**
 * 공개 색인 표면의 단일 원본 — robots.txt · sitemap.xml · canonical 이 전부 여기서 나와요.
 *
 * 게스트 링크(`/g/<token>`)와 공개 숙소 챗(`/p/<token>`)은 로그인 없이 예약자 이름·객실·
 * 대화를 보여주는 URL이고, 그 URL 토큰이 유일한 접근 통제예요. 그래서
 *  - 사이트맵에 토큰 경로를 절대 싣지 않고,
 *  - canonical 도 붙이지 않으며(`canonicalUrl` 이 허용목록 밖이면 null),
 *  - 페이지별 og 태그도 만들지 않아요 — 메신저 링크 미리보기는 메신저 서버가 대신
 *    열어보기 때문에 미리보기 카드 자체가 유출 통로예요.
 *
 * 실제 강제력은 nginx 응답 헤더에 있어요(`deployments/apps/web/nginx.conf` 의
 * `X-Robots-Tag` map + `src/lib/robots-noindex.test.ts`). robots.txt 는 권고일 뿐이에요.
 */

/** 빌드 때 `VITE_SITE_ORIGIN` 이 없으면 쓰는 값. 도메인을 적는 유일한 자리예요. */
export const DEFAULT_SITE_ORIGIN = "https://stayline.io";

/**
 * 색인을 허용하는 경로 — 마케팅·법무 페이지뿐이에요.
 * `App.tsx` 의 `PublicLayout` 아래 라우트와 같아야 해요.
 */
export const INDEXABLE_PATHS = ["/", "/guide", "/faq", "/terms", "/privacy"] as const;

export type IndexablePath = (typeof INDEXABLE_PATHS)[number];

/** 브랜드 접미사. 제목 끝에 한 번만 붙어요. */
const TITLE_SUFFIX = "스테이라인";

/**
 * 색인 허용 경로별 `<title>` · `<meta name="description">`.
 *
 * `INDEXABLE_PATHS` 와 키가 같아야 하고 `seo.test.ts` 가 그걸 잠가요 — 경로를 추가하면서
 * 여기를 빠뜨리면 그 페이지가 옆 페이지와 **똑같은 제목으로 검색 결과에 뜨는데, 그건
 * 화면에서는 절대 안 보이는 종류의 결함**이에요.
 *
 * 토큰 경로(`/g/`·`/p/`)는 여기 없고 앞으로도 없어야 해요. 제목에 예약자 이름이나 숙소가
 * 들어가는 순간 메신저 미리보기 카드가 그걸 실어 나르는데, 그 카드는 링크를 받은 사람이
 * 아니라 **메신저 서버**가 열어서 만들어요.
 */
export const PAGE_META: Record<IndexablePath, { title: string; description: string }> = {
  "/": {
    title: `숙소 안내를 게스트 언어로 자동 응대하는 ${TITLE_SUFFIX}`,
    description:
      "체크인 방법·와이파이·주변 정보를 게스트가 묻는 언어로 답해요. 예약이 들어오면 안내 링크가 자동으로 만들어져요.",
  },
  "/guide": {
    title: `사용 가이드 · ${TITLE_SUFFIX}`,
    description:
      "숙소 등록부터 예약 연동, 안내 문서 작성과 게스트 링크 발송까지 순서대로 안내해요.",
  },
  "/faq": {
    title: `자주 묻는 질문 · ${TITLE_SUFFIX}`,
    description: "채널 연동, 게스트 링크 만료, 대화 보관 기간에 대한 답을 모았어요.",
  },
  "/terms": {
    title: `이용약관 · ${TITLE_SUFFIX}`,
    description: "스테이라인 서비스 이용약관이에요.",
  },
  "/privacy": {
    title: `개인정보 처리방침 · ${TITLE_SUFFIX}`,
    description: "수집하는 개인정보 항목과 보유기간, 위탁·국외이전 현황을 적어 두었어요.",
  },
};

/** 경로에 해당하는 제목·설명. 색인 대상이 아니면 null (토큰 URL 에는 붙이지 않아요). */
export function pageMeta(pathname: string): { title: string; description: string } | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : "/";
  return PAGE_META[path as IndexablePath] ?? null;
}

/** 뒤 슬래시를 떼서 `origin + path` 가 항상 슬래시 하나로 이어지게 해요. */
export function normalizeOrigin(origin: string | undefined | null): string {
  const value = (origin ?? "").trim();
  return (value || DEFAULT_SITE_ORIGIN).replace(/\/+$/, "");
}

/** 색인 허용 경로면 절대 URL, 아니면 null (토큰 URL에는 canonical 을 붙이지 않아요). */
export function canonicalUrl(origin: string, pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : "/";
  return INDEXABLE_PATHS.some((p) => p === path) ? `${normalizeOrigin(origin)}${path}` : null;
}

/**
 * 허용목록 방식 robots.txt.
 *
 * 마지막 `Disallow: /` 가 기본 차단이라 새 화면이 생겨도 따로 손대지 않는 한 색인되지
 * 않아요(가장 긴 규칙이 이기므로 위의 `Allow` 들만 뚫려요).
 */
export function robotsTxt(origin: string): string {
  const site = normalizeOrigin(origin);
  return `# 스테이라인 robots.txt — 허용목록이에요. 아래 Allow 에 없는 경로는 맨 끝
# "Disallow: /" 에 걸려 기본 차단이라, 새 화면이 생겨도 손대지 않는 한 색인되지 않아요.
User-agent: *

# 마케팅·법무 페이지 (src/lib/seo.ts 의 INDEXABLE_PATHS)
Allow: /$
${INDEXABLE_PATHS.filter((p) => p !== "/")
  .map((p) => `Allow: ${p}`)
  .join("\n")}

# 렌더링에 필요한 정적 자원 — 막으면 크롤러가 SPA 를 그리지 못하고, 링크 미리보기
# 카드의 이미지도 못 가져와요.
Allow: /assets/
Allow: /brand/
Allow: /landing/
Allow: /guides/
Allow: /favicon
Allow: /apple-touch-icon.png

# /g/<token> · /p/<token> · /admin 은 일부러 Disallow 로 막지 않아요. 크롤러가 들어오지
# 못하면 nginx 가 내려주는 "X-Robots-Tag: noindex" 를 볼 수 없어서, 링크가 외부에 한 번
# 공유되면 내용 없이 URL(=토큰)만 색인될 수 있거든요. 들여보내고 noindex 로 확실히 빼는
# 쪽이 안전해요 — deployments/apps/web/nginx.conf 참고.
Allow: /g/
Allow: /p/
Allow: /admin

Disallow: /

Sitemap: ${site}/sitemap.xml
`;
}

/** 색인 허용 경로만 담은 사이트맵. 토큰 URL은 절대 들어가지 않아요. */
export function sitemapXml(origin: string): string {
  const site = normalizeOrigin(origin);
  const urls = INDEXABLE_PATHS.map((path) => `  <url><loc>${site}${path}</loc></url>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>
`;
}
